import type { Passenger, PassengersData } from '@aviata/booking-data';
import type { Airflow } from '@aviata/platform-types';
import { dateUtils, citizenshipEntity, providerClassLib } from 'shared';

import type { PassengerWithDocType } from '../types';
import { checkSpecialSymbolsInInitials } from './special-symbols';

type AgeBetween =
  Airflow.Offer.Schema.PassengerEntity['validators']['age_between'];

/**
 * Returns true when the passenger's age satisfies the schema's age bracket.
 *
 * `ageBetween.to_date` is used as the primary reference date (backend sets
 * this to the departure date, but it may reflect the booking date instead).
 * When `departureDate` is provided it is checked in addition to `to_date` so
 * that a passenger whose birthday falls on the flight day is not incorrectly
 * admitted to the wrong age tier (e.g. a child turning 12 on the departure
 * day must be treated as an adult regardless of what `to_date` says).
 *
 * Returns true when DOB, `to_date`, or the computed age is missing/invalid
 * so we never drop passengers from the saved-list for incomplete data.
 */
export const isPassengerInAgeBracket = (
  passenger: Passenger,
  ageBetween: AgeBetween,
  departureDate?: string,
) => {
  if (!passenger.dob || !ageBetween?.to_date) {
    return true;
  }
  const { age } = dateUtils.getAgeByDate(passenger.dob, ageBetween.to_date);
  if (!Number.isFinite(age)) {
    return true;
  }
  if (!(age >= ageBetween.min && age < ageBetween.max + 1)) {
    return false;
  }
  if (departureDate) {
    const { age: ageAtDeparture } = dateUtils.getAgeByDate(
      passenger.dob,
      departureDate,
    );
    if (!Number.isFinite(ageAtDeparture)) {
      return true;
    }
    return (
      ageAtDeparture >= ageBetween.min && ageAtDeparture < ageBetween.max + 1
    );
  }
  return true;
};

// Kazakh ID-card document type. For international flights only passports are
// accepted, so saved passengers stored under this type are hidden from the list.
const ID_DOCUMENT_TYPE = 'УЛ';

/**
 * Narrow the saved + local passenger pool down to the ones eligible for the
 * currently-active passenger key. Two filters:
 *   - On international flights, hide ID-card passengers (passport only).
 *   - Drop anyone whose age falls outside the schema's `age_between` bucket.
 *
 * Used by `PassengersSection` to build the saved-list bottom-sheet contents.
 */
export const filterSavedPassengersForKey = (
  passengers: Passenger[],
  options: {
    isDomestic: boolean;
    ageBetween?: AgeBetween;
    departureDate?: string;
  },
): Passenger[] =>
  passengers.filter(pax => {
    if (
      !options.isDomestic &&
      (pax as PassengerWithDocType).document.type === ID_DOCUMENT_TYPE
    ) {
      return false;
    }
    if (
      options.ageBetween &&
      !isPassengerInAgeBracket(pax, options.ageBetween, options.departureDate)
    ) {
      return false;
    }
    return true;
  });

/**
 * True when the passenger has every document field the booking flow
 * requires. Azerbaijani citizens additionally need `fin`; countries
 * without a series field skip that check. Used by the page-level
 * submit gate and by the saved-list "missing data" prompt.
 */
export const checkIfPassengerHasRequiredDocumentFields = (
  updated: PassengerWithDocType,
) => {
  const isCountryWithoutSeries =
    citizenshipEntity.config.countriesWithoutSeries.includes(
      updated.citizenship,
    );

  const hasBasicDocumentFields =
    updated.document.givenAt &&
    (isCountryWithoutSeries || updated.document.series) &&
    updated.document.organization &&
    updated.document.expiresAt;

  if (updated.citizenship === citizenshipEntity.config.AZERBAIJAN_CODE) {
    return hasBasicDocumentFields && updated.fin;
  }

  return hasBasicDocumentFields;
};

/**
 * Pure pre-flight checks that run *after* vuelidate has signed off but
 * *before* the save POST. Captures the two app-level gates the page used
 * to inline: special characters in first/last name, and (FunSun only)
 * required document fields. The caller switches on `kind` and renders
 * the appropriate side effect (warning modal / inline alert / proceed).
 */
export type ValidatePassengersBeforeSaveResult =
  | { kind: 'ok' }
  | { kind: 'specialSymbols' }
  | { kind: 'missingDocument' };

export const validatePassengersBeforeSave = (
  passengersState: PassengersData,
  options: { providerClass?: string },
): ValidatePassengersBeforeSaveResult => {
  if (checkSpecialSymbolsInInitials(passengersState)) {
    return { kind: 'specialSymbols' };
  }

  if (providerClassLib.isFunSun(options.providerClass ?? '')) {
    const hasMissingDocument = Object.values(passengersState).some(
      pax =>
        !checkIfPassengerHasRequiredDocumentFields(pax as PassengerWithDocType),
    );
    if (hasMissingDocument) {
      return { kind: 'missingDocument' };
    }
  }

  return { kind: 'ok' };
};
